import {
  AppLayout,
  AppSidebar,
  BundleHeader,
  EmptyState,
  LoadingPage,
  PageHeader,
  WidgetCard,
} from "shared/components";
import { SystemRestart } from "iconoir-react";
import { useTranslation } from "react-i18next";
import { useWidgets } from "apps/widgets/useWidgets.tsx";
import { Flex } from "styled-system/jsx";
import { useState } from "react";
import { StartNewWidgetModal } from "apps/widgets/components";
import {
  EnumListType,
  EnumOwnerType,
  EnumWidgetEventSettingsDisplayType,
  EnumWidgetType,
  useWidgetCreateMutation,
  WidgetManyDocument,
} from "gql/generated.ts";
import { useToastContext } from "@ttc3k/trekker";
import { createMultilingual } from "shared/utils";
import { useParams } from "react-router-dom";
import {
  BuildNewListModal,
  StartNewListModal,
} from "apps/customLists/components";

export const Widgets = () => {
  const { t } = useTranslation();
  const { toastFactory } = useToastContext();
  const { organizationId } = useParams();
  const {
    appViewSections,
    selectedAppItem,
    onAppViewChange,
    widgets,
    searchValue,
    onSearchValueChange,
    loading,
    error,
    onFavoriteClick,
    updatingWidgetId,
  } = useWidgets();

  /* States for creating new widgets without creating a new list */
  const [isStartNewWidgetModalOpen, setIsStartNewWidgetModalOpen] =
    useState(false);

  const [newWidgetDetails, setNewWidgetDetails] = useState({
    widgetType: EnumWidgetType.Operator,
    name: "",
    listId: "",
  });

  /* States for creating new lists for a widget */
  const [isStartNewListModalOpen, setIsStartNewListModalOpen] = useState(false);
  const [newListDetails, setNewListDetails] = useState({
    widgetType: EnumWidgetType.Operator,
    name: "",
    type: "",
  });

  const [createWidget, { loading: createWidgetLoading }] =
    useWidgetCreateMutation();

  const handleCreateWidget = async (
    widgetType: EnumWidgetType,
    name: string,
    listId?: string,
    eventSettings?: {
      displayType: EnumWidgetEventSettingsDisplayType;
      lookAheadMonths: number;
      eventTags: string[];
    },
  ) => {
    try {
      await createWidget({
        variables: {
          record: {
            type: widgetType,
            name: createMultilingual(name),
            list: listId,
            owner: {
              entityId: organizationId,
              type: EnumOwnerType.Organization,
            },
            description: createMultilingual(""),
            eventSettings: eventSettings,
          },
        },
        refetchQueries: [WidgetManyDocument],
      }).then((res) => {
        if (res.data && !res.errors) {
          toastFactory.create({
            title: t("core:SUCCESS"),
            description: t("widgets:CREATE_NEW.SUCCESS", {
              widgetName: name,
            }),
          });
        }
      });
    } catch {
      toastFactory.create({
        title: t("core:ERROR.TITLE"),
        description: t("widgets:CREATE_NEW.ERROR", { widgetName: name }),
      });
    }
  };

  if (loading) return <LoadingPage />;

  if (error) throw new Error(error.message);

  return (
    <>
      <AppLayout
        sidebar={
          <AppSidebar
            Icon={SystemRestart}
            headerTitle={t("widgets:HEADER.TITLE")}
            headerDescription={t("widgets:HEADER.DESCRIPTION")}
            primaryButtonCopy={t("widgets:HEADER.CTA_BUTTON_COPY")}
            primaryButtonProps={{
              visual: "accent.blue",
              onClick: () => setIsStartNewWidgetModalOpen((prev) => !prev),
            }}
            viewTitle={t("widgets:HEADER.VIEWS.TITLE")}
            viewSections={appViewSections}
            onViewSelectionChange={onAppViewChange}
          />
        }
        content={
          <>
            <PageHeader
              Icon={selectedAppItem.Icon}
              title={t(`widgets:HEADER.VIEWS.${selectedAppItem.key}.TITLE`)}
              description={t(
                `widgets:HEADER.VIEWS.${selectedAppItem.key}.DESCRIPTION`,
              )}
            />
            <BundleHeader
              searchProps={{
                value: searchValue,
                onValueChange: onSearchValueChange,
              }}
              count={widgets.length}
              Icon={selectedAppItem.Icon}
              title={t(`widgets:HEADER.VIEWS.${selectedAppItem.key}.TITLE`)}
            />
            <Flex flexDir="column" gap="150" flexGrow={1} marginTop="400">
              {widgets.length < 1 ? (
                <EmptyState text={t("widgets:PAGE.EMPTY")} />
              ) : (
                <>
                  {widgets.map((widget) => (
                    <WidgetCard
                      key={widget._id}
                      widget={widget}
                      onFavoriteClick={onFavoriteClick}
                      updatingWidgetId={updatingWidgetId}
                    />
                  ))}
                </>
              )}
            </Flex>
          </>
        }
      />
      <StartNewWidgetModal
        isOpen={isStartNewWidgetModalOpen}
        onClose={() => setIsStartNewWidgetModalOpen(false)}
        onNextClick={(widgetType, name, listId, eventSettings) => {
          if (name && listId !== "NEW_LIST") {
            handleCreateWidget(widgetType, name, listId, eventSettings).then(
              () => setIsStartNewWidgetModalOpen(false),
            );
          } else {
            setNewWidgetDetails({ widgetType, name, listId: "" });
            setIsStartNewWidgetModalOpen(false);
            setIsStartNewListModalOpen(true);
          }
        }}
        loadingCreate={createWidgetLoading}
      />
      <StartNewListModal
        isOpen={isStartNewListModalOpen}
        onClose={() => setIsStartNewListModalOpen(false)}
        onListTypeSelect={(type, name) => {
          setNewListDetails({
            widgetType: EnumWidgetType.Operator,
            name,
            type,
          });
          setIsStartNewListModalOpen(false);
        }}
      />
      <BuildNewListModal
        isOpen={
          newListDetails.name.length > 0 && newListDetails.type.length > 0
        }
        onClose={() =>
          setNewListDetails({
            widgetType: EnumWidgetType.Operator,
            name: "",
            type: "",
          })
        }
        name={newListDetails.name}
        type={newListDetails.type as EnumListType}
        widgetName={newWidgetDetails.name}
        onWidgetCreate={handleCreateWidget}
      />
    </>
  );
};
